import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { sendForgetPassword } from '@/lib/auth';
import { AlertCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '@/components/ui/alert';

const formSchema = z.object({
  email: z.string(),
});

export function ResetPassword() {
  const sendResetPassword = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      return sendForgetPassword(values.email);
    },
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });
  const onSubmit = (values: z.infer<typeof formSchema>) => {
    sendResetPassword.mutateAsync(values).catch(() =>
      form.setError('root', {
        message: 'Invalid email address.',
      })
    );
  };
  return (
    <div className="mx-auto grid w-[350px] gap-6">
      <div className="grid gap-2 text-center">
        <h1 className="text-3xl font-bold">Forgot password?</h1>
        <p className="text-muted-foreground">
          Enter the email address associated with your account and we will send
          you a link to reset your password.
        </p>
      </div>
      <Form {...form}>
        {sendResetPassword.isSuccess && (
          <Alert variant="success">
            We have emailed you a link to reset your password
          </Alert>
        )}
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
          <FormField
            control={form.control}
            disabled={sendResetPassword.isSuccess}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    inError={!!form.formState.errors.root}
                    placeholder="Email"
                    {...field}
                  />
                </FormControl>
                {form.formState.errors.root && (
                  <FormMessage className="flex text-xs gap-2">
                    <AlertCircleIcon className="h-4 w-4 items-center" />
                    {`${form.formState.errors.root.message}`}
                  </FormMessage>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={sendResetPassword.isSuccess}
          >
            Reset password
          </Button>
          <Link to="/login" className="w-fit">
            <Button variant="link" type="button">
              Back to login
            </Button>
          </Link>
        </form>
      </Form>
    </div>
  );
}
